/**
 * NOTE: Even those these interfaces are very similar to those in schema, we keep them separated for now
 *       to position ourselves to better handle changes to the backend serializers.
 */

import { QuestionCategory, QuestionCategory_values, QuestionType, QuestionType_values } from 'models/med_templates/enums'
import { string as dstring, number as dnumber, positiveInteger, boolean as dboolean, oneOf, nullable, array, exact, guard } from 'decoders'

export { QuestionCategory, QuestionType }

export interface QuestionChoice {
    question: number
    id: number
    title: string
    order: number
}

export interface Question {
    speciality: number
    id: number
    category: QuestionCategory
    title: string
    label: string | null
    type: QuestionType
    help_text: string | null
    choices: QuestionChoice[]
    link: string | null
    show_title_in_text_output: boolean
    hide: boolean
    allow_notes: boolean
    locked: boolean
}

export interface OrderedQuestion {
    question: Question
    show_if: number | null
}

export interface QuestionGroup {
    id: number
    title: string | null
    questions: OrderedQuestion[]
    category: QuestionCategory
    link: string | null
}

const qChoice = exact({
    question: dnumber,
    id: dnumber,
    title: dstring,
    order: positiveInteger,
})

export const question = exact({
    speciality: dnumber,
    id: dnumber,
    category: oneOf(QuestionCategory_values),
    title: dstring,
    label: nullable(dstring),
    type: oneOf(QuestionType_values),
    help_text: nullable(dstring),
    choices: array(qChoice),
    link: nullable(dstring),
    show_title_in_text_output: dboolean,
    hide: dboolean,
    allow_notes: dboolean,
    locked: dboolean
})

const orderedQuestion = exact({
    question,
    show_if: nullable(dnumber),
})

export const questionGroup = exact({
    id: dnumber,
    title: nullable(dstring),
    questions: array(orderedQuestion),
    category: oneOf(QuestionCategory_values),
    link: nullable(dstring),
})

export const questionDecoder = guard(question, { style: 'simple' })
export const qChoiceDecoder = guard(qChoice, { style: 'simple' })
export const qChoicesDecoder = guard(array(qChoice), { style: 'simple' })

// sanity checks that decoders/guards are not missing properties
try { const _orderedQuestion: OrderedQuestion = guard(orderedQuestion)({}) } catch(e) {}
try { const _questionGroup: QuestionGroup = guard(questionGroup)({}) } catch(e) {}
try { const _question: Question = questionDecoder({}) } catch(e) {}
try { const _qChoice: QuestionChoice = qChoiceDecoder({}) } catch(e) {}
try { const _qChoices: QuestionChoice[] = qChoicesDecoder({}) } catch(e) {}
